import React from 'react';
import { clsx } from 'clsx';
import { LucideIcon } from 'lucide-react';
import { usePageRBAC } from '../context/RBACContext';
import { useTranslation } from 'react-i18next';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    variant?: 'primary' | 'secondary' | 'danger' | 'ghost' | 'outline';
    size?: 'sm' | 'md' | 'lg';
    fullWidth?: boolean;
    icon?: LucideIcon;
    isLoading?: boolean;
    children: React.ReactNode;
    bypassRBAC?: boolean;
}

export const Button: React.FC<ButtonProps> = ({
    variant = 'primary',
    size = 'md',
    fullWidth = false,
    className,
    icon: Icon,
    children,
    isLoading = false,
    bypassRBAC = false,
    disabled,
    ...props
}) => {
    const { isDisabled } = usePageRBAC();
    const { t } = useTranslation();
    const isActuallyDisabled = disabled || (isDisabled && !bypassRBAC);

    const baseStyles = 'inline-flex items-center justify-center font-bold tracking-wide transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap';
    const variants = {
        primary: 'bg-brand-600 text-white shadow-[0_8px_30px_rgb(0,0,0,0.12)] hover:bg-brand-700 hover:shadow-[0_8px_30px_rgb(0,0,0,0.16)] hover:-translate-y-0.5 border border-transparent',
        secondary: 'bg-transparent border border-slate-200 text-slate-600 hover:bg-slate-50 hover:text-slate-900 shadow-sm focus:ring-slate-200',
        outline: 'bg-transparent border border-brand-200 text-brand-600 hover:bg-brand-50 hover:text-brand-700 focus:ring-brand-200',
        danger: 'bg-rose-500 text-white shadow-[0_8px_30px_rgb(225,29,72,0.12)] hover:bg-rose-600 hover:shadow-[0_8px_30px_rgb(225,29,72,0.16)] hover:-translate-y-0.5 border border-transparent focus:ring-rose-500',
        ghost: 'bg-transparent text-slate-600 hover:bg-slate-100 hover:text-slate-900 focus:ring-slate-500',
    };
    const sizes = {
        sm: 'px-4 py-2 text-xs rounded-2xl',
        md: 'px-6 py-3 text-sm rounded-2xl',
        lg: 'px-8 py-4 text-base rounded-2xl',
    };

    return (
        <button
            className={clsx(
                baseStyles,
                variants[variant],
                sizes[size],
                fullWidth && 'w-full',
                className
            )}
            disabled={isActuallyDisabled || isLoading}
            title={isActuallyDisabled && !disabled ? t('common.actionDisabled', 'Action disabled for this role') : props.title}
            {...props}
        >
            {isLoading ? (
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-current" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
            ) : Icon && <Icon className={clsx("mr-2 flex-shrink-0", size === 'sm' ? 'w-4 h-4' : 'w-5 h-5')} />}
            {children}
        </button>
    );
};
